import { useEffect, useMemo, useRef, useState } from "react";
import {
  isApiError,
  mergeBookingCancellationResponse,
  requiresExactCancellationRetry,
} from "@bw/shared/api";
import type {
  BookingCancelRequest,
  BookingResponse,
} from "@bw/shared/types/generated";
import { useAuth, useFacilityNames } from "@bw/shared/hooks";
import { groupBookingSets } from "@bw/shared/utils";
import {
  Alert,
  CircularProgress,
  List,
  Stack,
  Typography,
} from "@mui/material";
import CardMembershipRounded from "@mui/icons-material/CardMembershipRounded";
import ReceiptLongRounded from "@mui/icons-material/ReceiptLongRounded";
import { NavRow } from "../../../common/components/NavRow";
import { Page } from "../../../common/components/Page";
import { MaterialBookingSetCard } from "../../../common/components/MaterialBookingSetCard";
import { useAllBookings } from "../hooks/useAllBookings";
import { useCancellationQuote } from "../hooks/useCancellationQuote";
import { useCancelBooking } from "../hooks/useCancelBooking";
import { CalendarBookingRow } from "../components/CalendarBookingRow";
import { CancelBookingDialog } from "../components/CancelBookingDialog";

/**
 * The player's CROSS-FACILITY bookings calendar. Route /me/bookings.
 *
 * Lists reservations the token user hosts or currently participates in across
 * ALL facilities (GET /me/bookings, server-scoped + server-ordered), split into
 * Upcoming vs Past by start_time. Each row shows the facility name (resolved
 * from the real facility endpoint), court, time, scoped amount, and status.
 *
 * Only a host row offers cancellation. Opening its dialog requests a short-lived
 * server quote; confirming commits that exact quote with an idempotency key.
 */
export function CalendarPage() {
  const { user } = useAuth();
  const viewerUserId = user?.uid;
  const bookingsQ = useAllBookings();

  // The booking whose cancel dialog is open (null = closed).
  const [target, setTarget] = useState<BookingResponse | null>(null);
  const [quoteRequestVersion, setQuoteRequestVersion] = useState(0);
  const [decisionChanged, setDecisionChanged] = useState(false);
  const [cancellationNotice, setCancellationNotice] = useState("");
  const cancellationNoticeRef = useRef<HTMLDivElement>(null);
  const cancellationRequestRef = useRef<BookingCancelRequest | null>(null);
  // Server responses for bookings just cancelled this session — they carry the
  // authoritative refund_amount + cancelled_at + status, which we overlay onto
  // the list so the row reflects the real outcome (not the preview).
  const [cancelled, setCancelled] = useState<Record<string, BookingResponse>>(
    {},
  );

  const targetFacilityId = target?.facility_id;
  const cancel = useCancelBooking(targetFacilityId);
  const quoteQ = useCancellationQuote(
    target?.booking_id,
    quoteRequestVersion,
    Boolean(target),
  );

  // Overlay just-cancelled rows, keep the server's start_time order. The list is
  // already ordered by start_time server-side; the overlay never reorders it.
  const rows = useMemo(() => {
    const list = bookingsQ.data ?? [];
    return list.map((b) => cancelled[b.booking_id] ?? b);
  }, [bookingsQ.data, cancelled]);

  // Resolve every distinct facility id to its real name (one query per venue).
  const facilityIds = useMemo(
    () => [...new Set(rows.map((b) => b.facility_id))],
    [rows],
  );
  const facilityNames = useFacilityNames(facilityIds);

  useEffect(() => {
    if (!target) return;
    const canonical = rows.find(
      (booking) => booking.booking_id === target.booking_id,
    );
    if (
      canonical?.status === "confirmed" &&
      canonical.cancellation_recovery_pending !== true
    ) {
      setTarget(canonical);
      return;
    }
    cancellationRequestRef.current = null;
    setDecisionChanged(false);
    cancel.reset();
    setTarget(null);
    setCancellationNotice(
      canonical
        ? "The booking status changed. Cancellation controls closed."
        : "The booking is no longer available. Cancellation controls closed.",
    );
  }, [cancel, rows, target?.booking_id]);

  useEffect(() => {
    if (cancellationNotice) cancellationNoticeRef.current?.focus();
  }, [cancellationNotice]);

  // Split Upcoming vs Past by start_time. A row whose start has passed (or whose
  // start is unparseable) falls into Past — the same conservative gate the row
  // uses for cancellability. The server order (by start_time) is preserved within
  // each group; Past is reversed so the most recent shows first.
  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const up: BookingResponse[] = [];
    const pa: BookingResponse[] = [];
    for (const b of rows) {
      const ms = Date.parse(b.start_time);
      if (!Number.isNaN(ms) && ms > now) up.push(b);
      else pa.push(b);
    }
    return { upcoming: up, past: [...pa].reverse() };
  }, [rows]);

  function handleConfirmCancel() {
    if (!target || !quoteQ.data) return;
    const request = cancellationRequestRef.current ?? {
      quote_id: quoteQ.data.quote_id,
      idempotency_key: crypto.randomUUID(),
    };
    cancellationRequestRef.current = request;
    setDecisionChanged(false);
    cancel.mutate(
      {
        bookingId: target.booking_id,
        request,
      },
      {
        onSuccess: (res) => {
          setCancelled((prev) => ({
            ...prev,
            [res.booking_id]: mergeBookingCancellationResponse(target, res),
          }));
          setTarget(null);
        },
        onError: async (error) => {
          if (isApiError(error) && error.details?.refresh_required === true) {
            cancellationRequestRef.current = null;
            const refreshed = await bookingsQ.refetch();
            if (refreshed.isError) {
              setTarget(null);
              setCancellationNotice(
                "The current booking status could not be confirmed. Cancellation controls closed.",
              );
              return;
            }
            const canonical = refreshed.data?.find(
              (booking) => booking.booking_id === target.booking_id,
            );
            if (
              canonical?.status === "confirmed" &&
              canonical.cancellation_recovery_pending !== true
            ) {
              setTarget(canonical);
              setDecisionChanged(true);
              setQuoteRequestVersion((version) => version + 1);
            } else {
              cancel.reset();
              setDecisionChanged(false);
              setTarget(null);
              setCancellationNotice(
                canonical
                  ? "The booking status changed. Cancellation controls closed."
                  : "The booking is no longer available. Cancellation controls closed.",
              );
            }
          }
        },
      },
    );
  }

  function openDialog(booking: BookingResponse) {
    cancel.reset();
    cancellationRequestRef.current = null;
    setDecisionChanged(false);
    setCancellationNotice("");
    setQuoteRequestVersion((version) => version + 1);
    setTarget(booking);
  }

  function closeDialog() {
    if (requiresExactCancellationRetry(cancel.error)) return;
    cancellationRequestRef.current = null;
    setDecisionChanged(false);
    setTarget(null);
  }

  function refreshQuote() {
    if (requiresExactCancellationRetry(cancel.error)) return;
    cancellationRequestRef.current = null;
    cancel.reset();
    setDecisionChanged(false);
    setQuoteRequestVersion((version) => version + 1);
  }

  // ---- List states ----------------------------------------------------------
  let body: React.ReactNode;
  if (bookingsQ.isLoading) {
    body = (
      <Stack
        direction="row"
        spacing={1.5}
        role="status"
        sx={{ alignItems: "center" }}
      >
        <CircularProgress size={20} />
        <Typography color="text.secondary">Loading your bookings…</Typography>
      </Stack>
    );
  } else if (bookingsQ.isError) {
    body = (
      <Alert severity="error">
        Something went wrong loading your bookings. Please try again.
      </Alert>
    );
  } else if (rows.length === 0) {
    body = (
      <Alert severity="info" role="status">
        No bookings yet. Reserve a court and it shows up here.
      </Alert>
    );
  } else {
    body = (
      <Stack spacing={2.5}>
        <BookingGroup
          title="Upcoming"
          bookings={upcoming}
          facilityNames={facilityNames}
          viewerUserId={viewerUserId}
          onCancel={openDialog}
          emptyLabel="No upcoming bookings."
        />
        <BookingGroup
          title="Past"
          bookings={past}
          facilityNames={facilityNames}
          viewerUserId={viewerUserId}
          onCancel={openDialog}
          emptyLabel="No past bookings."
        />
      </Stack>
    );
  }

  return (
    <Page title="Bookings & receipts" narrow>
      {cancellationNotice ? (
        <Alert
          severity="info"
          ref={cancellationNoticeRef}
          role="status"
          aria-live="polite"
          tabIndex={-1}
          sx={{ mb: 2 }}
        >
          {cancellationNotice}
        </Alert>
      ) : null}
      {body}
      <nav aria-label="Memberships and receipts">
        <List disablePadding>
          <NavRow icon={<CardMembershipRounded />} label="Memberships" to="/me/memberships" />
          <NavRow icon={<ReceiptLongRounded />} label="Receipts & transactions" to="/me/transactions" />
        </List>
      </nav>

      {target ? (
        <CancelBookingDialog
          booking={target}
          quote={quoteQ.data}
          quoteLoading={quoteQ.isFetching}
          quoteError={quoteQ.error}
          onQuoteRetry={refreshQuote}
          onConfirm={handleConfirmCancel}
          onClose={closeDialog}
          cancelPending={cancel.isPending}
          cancelError={cancel.error}
          decisionChanged={decisionChanged}
        />
      ) : null}
    </Page>
  );
}

/**
 * A titled section of bookings (Upcoming / Past). The rows are GROUPED into sets
 * by recurring_group_id (a multi-court / recurring reservation collapses into ONE
 * summary card; a lone booking stays a single row exactly as before) so a set of
 * courts reads as one reservation, matching the "invite the whole set" behaviour.
 * Renders an honest per-section empty line when the group has no rows so the
 * heading is never an empty promise.
 *
 * A multi-booking set → ONE BookingSetCard (facility + adaptive label + single
 * total + a single "View" into the set detail; NO per-set Cancel — a reservation
 * is cancelled per-court on the detail page). A single booking → its existing
 * CalendarBookingRow (View + Cancel), unchanged.
 */
function BookingGroup({
  title,
  bookings,
  facilityNames,
  viewerUserId,
  onCancel,
  emptyLabel,
}: {
  title: string;
  bookings: BookingResponse[];
  facilityNames: Map<string, string>;
  viewerUserId: string | undefined;
  onCancel: (booking: BookingResponse) => void;
  emptyLabel: string;
}) {
  // Group WITHIN this section (each of Upcoming / Past) so a set's rows stay in
  // their section; server order is preserved (a set sits at its first appearance).
  const sets = groupBookingSets(bookings);
  return (
    <Stack component="section" spacing={0.5}>
      <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 700, px: { xs: 0.5, sm: 0 } }}>
        {title}
      </Typography>
      {bookings.length > 0 ? (
        <List disablePadding>
          {sets.map((set) =>
            set.isGroup ? (
              <MaterialBookingSetCard
                key={set.key}
                set={set}
                facilityName={facilityNames.get(set.anchor.facility_id)}
              />
            ) : (
              <CalendarBookingRow
                key={set.anchor.booking_id}
                booking={set.anchor}
                facilityName={facilityNames.get(set.anchor.facility_id)}
                isHost={
                  Boolean(viewerUserId) && set.anchor.user_id === viewerUserId
                }
                onCancel={onCancel}
              />
            ),
          )}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      )}
    </Stack>
  );
}
